/**
 * Badge tones (TASK-5133, DEV_RULES §10.10) — the ONLY colours a status or
 * type badge may use. Lives in its own module (not in StatusBadge.tsx) so
 * components that draw their own pill shape can take the classes without
 * tripping React Fast Refresh.
 *
 * Semantic tones (fixed, encode meaning — never dealer-overridable):
 *   neutral  — draft, unknown, inactive, archived
 *   info     — planned, informational, "new"
 *   success  — done, paid, confirmed, healthy
 *   warning  — needs attention, pending, visited, return
 *   danger   — failed, rejected, overdue, critical
 *   progress — under way / in progress
 *
 * Categorical tones `kind-1`..`kind-8` tell TYPES apart and carry no meaning
 * (permanent order, provider type, audit target…). They are the identity
 * palette `avatar-1`..`avatar-8` — one fixed system palette for "this is a
 * different kind of thing", whether the thing is a person or an object.
 */
export type SemanticTone =
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "progress";

export type KindTone =
  | "kind-1"
  | "kind-2"
  | "kind-3"
  | "kind-4"
  | "kind-5"
  | "kind-6"
  | "kind-7"
  | "kind-8";

export type BadgeTone = SemanticTone | KindTone;

// Full class strings, spelled out so Tailwind's scanner sees every one.
const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: "bg-tone-neutral-bg text-tone-neutral-fg",
  info: "bg-tone-info-bg text-tone-info-fg",
  success: "bg-tone-success-bg text-tone-success-fg",
  warning: "bg-tone-warning-bg text-tone-warning-fg",
  danger: "bg-tone-danger-bg text-tone-danger-fg",
  progress: "bg-tone-progress-bg text-tone-progress-fg",
  "kind-1": "bg-avatar-1-bg text-avatar-1-fg", // blue
  "kind-2": "bg-avatar-2-bg text-avatar-2-fg", // violet
  "kind-3": "bg-avatar-3-bg text-avatar-3-fg", // emerald
  "kind-4": "bg-avatar-4-bg text-avatar-4-fg", // orange
  "kind-5": "bg-avatar-5-bg text-avatar-5-fg", // pink
  "kind-6": "bg-avatar-6-bg text-avatar-6-fg", // cyan
  "kind-7": "bg-avatar-7-bg text-avatar-7-fg", // amber
  "kind-8": "bg-avatar-8-bg text-avatar-8-fg", // rose
};

// Solid dot in the tone's fg colour, so a status dot always matches its pill.
const TONE_DOT_CLASSES: Record<SemanticTone, string> = {
  neutral: "bg-tone-neutral-fg",
  info: "bg-tone-info-fg",
  success: "bg-tone-success-fg",
  warning: "bg-tone-warning-fg",
  danger: "bg-tone-danger-fg",
  progress: "bg-tone-progress-fg",
};

/** `bg-…` class for a status dot drawn in a semantic tone's fg colour. */
export function toneDotClass(tone: SemanticTone): string {
  return TONE_DOT_CLASSES[tone];
}

/** `bg-… text-…` classes for a badge tone. */
export function toneClasses(tone: BadgeTone): string {
  return TONE_CLASSES[tone];
}
